import React, { useState, useRef, useEffect, useCallback } from "react";
import { cn } from "../../../utils/cn.ts";
import { Input } from "../../../components/inputs/input";
import { FontStyles } from "../../../utils/font-styles";
import { focusStyles } from "../../../utils/focus-styles";
import { useSolicitorNameSearch } from "./use-solicitor-name-search";
import { ExistingSolicitorResult } from "./types";
import "../../../../assets/fa-icons/css/fontawesome.css";
import "../../../../assets/fa-icons/css/sharp-regular.css";

interface ExistingSolicitorSearchProps {
  onSelect: (solicitor: ExistingSolicitorResult) => void;
  error?: string;
}

export const ExistingSolicitorSearch: React.FC<ExistingSolicitorSearchProps> = ({ onSelect, error }) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const { solicitors, isLoading } = useSolicitorNameSearch(query, true);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasSearched = useRef(false);

  const SpinnerIcon = useCallback(
    () => <i className="fa-sharp fa-regular fa-spinner-third text-base animate-spin" />,
    [],
  );

  // Track when a search has been initiated
  useEffect(() => {
    if (isLoading) {
      hasSearched.current = true;
    }
  }, [isLoading]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(value.length >= 2);
  };

  const handleSelect = (solicitor: ExistingSolicitorResult) => {
    onSelect(solicitor);
    setQuery("");
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const showDropdown = isOpen && query.length >= 2;
  const showNoResults = showDropdown && !isLoading && solicitors.length === 0 && hasSearched.current;
  const showResults = showDropdown && solicitors.length > 0;

  return (
    <div className="w-full flex flex-col">
      <div className="relative w-full" ref={dropdownRef}>
        <Input
          ref={inputRef}
          type="text"
          label="Search for your solicitor by name"
          placeholder="Enter solicitor name"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          state={error ? "error" : "default"}
          showRequiredAsterisk={true}
          renderLeadingIcon={() => (
            <i className={`fa-sharp fa-regular fa-magnifying-glass ${FontStyles["body-l"]} text-light-slate`} />
          )}
          renderTrailingIcon={isLoading ? SpinnerIcon : undefined}
        />

        {(showResults || showNoResults) && (
          <div className="absolute z-50 w-full bg-neutral-white border border-foggy-firth rounded-md shadow-lg max-h-60 overflow-y-auto">
            {showNoResults && (
              <div className={`p-4 ${FontStyles["body-md"]} text-light-slate text-center`}>No results found</div>
            )}

            {showResults && (
              <ul className="py-1">
                {solicitors.map((solicitor) => (
                  <li key={solicitor.firmId}>
                    <button
                      type="button"
                      className={cn(
                        `w-full text-left px-4 py-3 ${FontStyles["body-md"]} text-slate hover:bg-north-sea-50 focus:bg-north-sea-50 transition-colors`,
                        focusStyles,
                      )}
                      onClick={() => handleSelect(solicitor)}
                    >
                      {solicitor.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {error && <span className={`${FontStyles["body-s"]} text-brick mt-1.5`}>{error}</span>}
    </div>
  );
};
